import { Component, OnInit, ViewChild } from '@angular/core';
import { OnyxEvent } from '../models/event';
import { FormGroup, FormBuilder } from '@angular/forms';
import { EventStore } from '../service/event.store';
import { Observable } from 'rxjs/Observable';
import { Router, ActivatedRoute } from '@angular/router';
import { MenuStore } from '../../menus/service/menu.store';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { Party } from '../models/party';
import { Contact } from '../models/contact';
import { RouteUtils } from '../utils/route-utils';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalConfig } from '../../shared/confirmation-modal/confirmation-modal-config';
import { TranslateService } from '@ngx-translate/core';
import { EventSearchParametersStore } from '../service/event-search-parameters.store';
import { PaymentStore } from '../service/payment.store';
import { of } from 'rxjs/observable/of';
import { RatePlanCode } from '../models/rate-plan-code';
import { AuthenticationStore } from '../../permission/services/authentication.store';
import { User } from 'app/auth/models/User';
import { MultiAgency } from 'app/shared/models/multi-agency';
import { UserLocalStorageService } from 'app/permission/services/user.local.storage.service';
import { MultiAgencyService } from 'app/shared/multi-agency/multi-agency.service';

@Component({
  selector: 'pioneer-event',
  templateUrl: './event.component.html'
})
export class EventComponent implements OnInit {
  public eventForm: FormGroup;

  public onyxEvent: OnyxEvent;

  public isReadOnly = true;

  public isBasicEventDataReadOnly = true;

  private user: User;

  public clientContactEditable = true;

  public propertyEmail: string;

  public agencyEmail: string;

  hasFinalPayment: boolean;

  editContactException = false;

  isRequestedOrRequestResentOrRejected: boolean;

  @ViewChild(ConfirmationModalComponent)
  confirmationModal: ConfirmationModalComponent;

  confirmationModalConfig = new ConfirmationModalConfig();

  constructor(public router: Router,
              private eventStore: EventStore,
              private formBuilder: FormBuilder,
              private menuStore: MenuStore,
              private flashMessagesService: FlashMessagesService,
              private translateService: TranslateService,
              private route: ActivatedRoute,
              private eventSearchParametersStore: EventSearchParametersStore,
              private paymentStore: PaymentStore,
              private authenticationStore: AuthenticationStore,
              private userLocalStorageService: UserLocalStorageService,
              private multiAgencyService: MultiAgencyService) {
  }

  ngOnInit() {
    this.menuStore.setPageHeader('EVENT.EVENT_INFO_TITLE', false);
    this.menuStore.setIsReadOnly(true);

    this.authenticationStore.user.subscribe((user: User) => {
      this.user = user;
    });

    this.onyxEvent = new OnyxEvent();
    this.eventStore.selectedEvent.subscribe(value => {
      this.onyxEvent = new OnyxEvent(value);
    });

    this.menuStore.isReadOnly.subscribe(isReadOnly => {
      this.isReadOnly = isReadOnly;
      this.isBasicEventDataReadOnly = isReadOnly ||
        // If the user has EditEventNonSubscriber permission and the eventProperty is subscriber,
        // the basic event data is readonly
        (this.onyxEvent.eventProperty.subscriber && this.authenticationStore.hasPermission('EditEventNonSubscriber'));
    });

    if (this.onyxEvent.name) {
      this.menuStore.setPageHeader(this.onyxEvent.name, this.onyxEvent.eventProperty.subscriber);
    }

    this.eventForm = this.formBuilder.group({
      'event': this.formBuilder.group({
        'eventAgency': this.formBuilder.group({}),
        'eventClient': this.formBuilder.group({}),
        'eventProperty': this.formBuilder.group({})
      })
    });

    // toggle page to editable if the URL ends with "edit" (it will default to read only)
    // otherwise, check if we need to show a warning flash message when in view modeFD
    RouteUtils.getLastUrlSegment(this.route)
      .subscribe(lastSegment => {
        if (lastSegment && lastSegment.path === 'edit') {
          this.togglePageToReadOnly(false);
        }
      });

    if (!this.onyxEvent.eventId) {
      this.hasFinalPayment = false;
      this.isRequestedOrRequestResentOrRejected = false;
    } else {
      this.paymentStore.getLatestFinalPayment().take(1).subscribe(lastFinalPayment => {
        this.hasFinalPayment = Boolean(this.hasFinalPayment || (lastFinalPayment && lastFinalPayment.eventPaymentId));
      });

      this.paymentStore.isLastPaymentRequestedOrRequestResentOrRequestRejected.subscribe(isReqOrReqResentOrRej => {
        this.isRequestedOrRequestResentOrRejected = isReqOrReqResentOrRej;
      });
    }

    this.authenticationStore.clientContactEditable$.subscribe(value => {
      this.clientContactEditable = value;
    });

    if (this.allowEditException()) {
      this.editContactException = this.allowEditException();
    }
  }

  save(model: any): Observable<OnyxEvent> {
    this.eventForm.disable();

    model.event.eventAgency = model.eventAgency;
    model.event.eventClient = model.eventClient;
    model.event.eventProperty = model.eventProperty;

    let formModel = Object.assign({}, this.onyxEvent.toJS(), model.event);
    const updatedFields: Object = {};

    if (formModel.eventAgency) {
      const eventAgency = this.onyxEvent.eventAgency || new Party({ contact: new Contact() });
      updatedFields[ 'agencyId' ] = formModel.eventAgency.agencyId;
      updatedFields[ 'eventAgency' ] = Object.assign({}, eventAgency, model[ 'eventAgency' ]);
      updatedFields[ 'eventAgency' ][ 'contact' ] = Object.assign({}, eventAgency.contact, model[ 'eventAgency' ][ 'contact' ]);
    }

    if (formModel.eventClient) {
      const eventClient = this.onyxEvent.eventClient || new Party({ contact: new Contact() });
      updatedFields[ 'clientReferenceNumber' ] = formModel.eventClient.clientReferenceNumber;
      updatedFields[ 'eventClient' ] = Object.assign({}, eventClient, model[ 'eventClient' ]);
      updatedFields[ 'eventClient' ][ 'contact' ] = Object.assign({}, eventClient.contact, model[ 'eventClient' ][ 'contact' ]);
    }

    if (formModel.eventProperty) {
      const eventProperty = this.onyxEvent.eventProperty || new Party({ contact: new Contact() });
      updatedFields[ 'brand' ] = formModel.eventProperty.brand;
      updatedFields[ 'chain' ] = formModel.eventProperty.chain;
      updatedFields[ 'propertyId' ] = formModel.eventProperty.propertyId;
      updatedFields[ 'eventProperty' ] = Object.assign({}, eventProperty, model[ 'eventProperty' ]);
      updatedFields[ 'eventProperty' ][ 'contact' ] = Object.assign({}, eventProperty.contact, model[ 'eventProperty' ][ 'contact' ]);
    }

    updatedFields[ 'startDate' ] = new Date(formModel.startDate).toISOString();
    updatedFields[ 'endDate' ] = new Date(formModel.endDate).toISOString();

    // checks if the user did not click the add button
    if (model.event.ratePlanCode) {
      formModel.ratePlanCodes.push(new RatePlanCode({ ratePlanCode: model.event.ratePlanCode }));
    }

    const updatedModel = new OnyxEvent(Object.assign({}, formModel, updatedFields));

    let returnedEvent;
    if (this.authenticationStore.isPropertyUser()) {
      updatedModel.eventProperty.eventReferenceNumber = formModel.groupCodeNumber;
      returnedEvent = this.eventStore.updateEvent(updatedModel);
    } else {
      updatedModel.eventAgency.eventReferenceNumber = formModel.groupCodeNumber;
      returnedEvent = this.eventStore.updateAgencyEvent(updatedModel);
    }


    returnedEvent.subscribe(
      () => {
        let changeIata = false;
        if (this.user && this.user.allArcIatas && this.user.customerInfo && this.user.customerInfo.length >= 2) {
          const key = this.user.username.toUpperCase() + AuthenticationStore.CURRENT_AGENCY_STR;
          const currentAgency = JSON.parse(this.userLocalStorageService.getItem(key));
          if (currentAgency.agencyId !== updatedModel.agencyId) {
            changeIata = true;
          }
        }
        if (changeIata) {
          const agency: MultiAgency = new MultiAgency({
            agencyId: updatedModel.agencyId,
            agencyName: updatedModel.eventAgency.name
          });
          this.authenticationStore.updateIataFromToken(agency.agencyId).subscribe(() => {
            this.multiAgencyService.setAgencySelectedUI(true);
            this.multiAgencyService.setDefaultAgency(agency);
            this.router.navigate(['/event', this.onyxEvent.eventId, 'summary']);
          });
        } else {
          this.router.navigate(['/event', this.onyxEvent.eventId, 'summary']);
        }
      },
      error => {
        this.eventForm.enable();
        if (error.includes('Agency Event Reference Number already exists')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_DUPLICATED_AGENCY_REFERENCE_NUMBER', error);
        } else if (error.includes('Property Event Reference Number already exists')) {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT_DUPLICATED_PROPERTY_REFERENCE_NUMBER', error);
        } else {
          this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_EVENT', error);
        }
      });

    return returnedEvent;
  }

  edit(): void {
    this.router.navigate(['/event', this.onyxEvent.eventId, 'summary', 'edit']);
  }


  confirmBeforeDeleting() {
    // set up the title of the confirmation modal
    const label = 'EVENT.DELETE_EVENT';
    this.confirmationModalConfig.title = this.translateService.instant(label);

    this.confirmationModal.open();
  }

  confirmBeforeDeletingHandler(value: any) {
    if (value) {
      const eventId = this.onyxEvent.eventId;
      this.delete();
    }
  }

  get canBeDeleted(): boolean {
    return this.onyxEvent.canBeDeleted;
  }

  delete(): void {
    this.eventForm.disable();
    this.eventStore.deleteEvent(this.onyxEvent.eventId).subscribe(() => {
      this.eventSearchParametersStore.updateUrl();
    }, () => {
      this.eventForm.enable();
    });
  }

  cancel(): void {
    this.router.navigate(['/event', this.onyxEvent.eventId, 'summary']);
    this.eventStore.selectedEvent.subscribe(event => {
      this.onyxEvent = new OnyxEvent(event);
    });
  }

  togglePageToReadOnly(readOnly: boolean): void {
    if (this.onyxEvent.name) {
      this.menuStore.setPageHeader(readOnly ? this.onyxEvent.name : 'Edit - ' + this.onyxEvent.name,
        this.onyxEvent.eventProperty.subscriber);
    } else {
      this.menuStore.setPageHeader(readOnly ? 'EVENT.EVENT_INFO_TITLE' : 'EVENT.TITLE_EDIT', false);
    }

    this.menuStore.setIsReadOnly(readOnly);
  }

  setupPropertyEmail(email: string): void {
    this.propertyEmail = email;
  }

  setupAgencyEmail(email: string): void {
    this.agencyEmail = email;
  }

  get hideEditButton(): Observable<boolean> {
    return of(Boolean(this.hasFinalPayment && !this.allowEditException()));
  }

  public allowEditException(): boolean {
    // Show Exception ((Requested or Resent Request or Rejected) and Role(new role for agency and support) and !GroupPayHotel)
    let editRequestedRole: boolean = this.authenticationStore.isAgencyUser() || this.authenticationStore.isSupportUser();
    let groupPayHotel: boolean = this.onyxEvent.eventProperty.subscriber;

    return this.isRequestedOrRequestResentOrRejected && editRequestedRole && !groupPayHotel;
  }

}




// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/event/event.component.ts